import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { 
  ArrowLeft, Layers, Filter, CheckCircle2, Shield, Zap, 
  LayoutList, SortAsc, HelpCircle, Briefcase, AlertTriangle,
  Clock, GitPullRequest, Users, Search, Globe, Settings, Sparkles, AlertCircle, LayoutDashboard
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { QUEUE_TEMPLATES } from '../constants/queueTemplates';
import { CANONICAL_INTENT_ALIGNMENTS } from '../constants/intentAlignments';
import type { QueueTemplate, QueueSelection } from '../types/queue';
import { CustomQueuesPanel } from './CustomQueuesPanel';
import { queueConfigOf, withQueueConfig, type CustomQueue, type QueueConfig } from '../utils/queueLibrary';

interface QueueLibraryViewProps {
  companyId: string;
  sowId: string;
  accessToken: string;
  currentUser: any;
}

export function QueueLibraryView({ companyId, sowId, accessToken, currentUser }: QueueLibraryViewProps) {
  const navigate = useNavigate();
  const [selections, setSelections] = useState<QueueSelection[]>([]);
  /** RESTORE_PLAN R1.6: free-typed queues beside the library (the old QueueManagement) */
  const [custom, setCustom] = useState<CustomQueue[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Config Modal State
  const [activeQueueId, setActiveQueueId] = useState<string | null>(null);
  const [configJql, setConfigJql] = useState('');
  const [aiResponse, setAiResponse] = useState<{explanation: string, confidence: string, assumptions: string[]} | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showJql, setShowJql] = useState(false);

  // Load Selections
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${companyId}/${sowId}`, {
            headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        if (res.ok) {
            const data = await res.json();
            const queueConfig = queueConfigOf(data.engagementConfig);
            setSelections(queueConfig.selections);
            setCustom(queueConfig.custom);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [companyId, sowId, accessToken]);

  const saveSelections = (newSelections: QueueSelection[]) => saveQueueConfig({ selections: newSelections, custom });
  const saveCustom = (newCustom: CustomQueue[]) => saveQueueConfig({ selections, custom: newCustom });

  /** Read the config, replace the queues as a whole (library switches and custom queues), write it back. */
  const saveQueueConfig = async (queues: QueueConfig) => {
      try {
          // Fetch current config first
          const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${companyId}/${sowId}`, {
              headers: { 'Authorization': `Bearer ${accessToken}` }
          });
          if (!res.ok) throw new Error("Failed to fetch");
          const { engagementConfig } = await res.json();

          const updatedConfig = withQueueConfig(engagementConfig, queues);

          const saveRes = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${companyId}/${sowId}`, {
              method: 'PUT',
              headers: { 
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify(updatedConfig)
          });

          if (saveRes.ok) {
              setSelections(queues.selections);
              setCustom(queues.custom);
              toast.success("Queue configuration saved");
              return true;
          }
          const err = await saveRes.json().catch(() => ({}));
          toast.error(err.error || "Failed to save queue configuration");
          return false;
      } catch (e) {
          console.error(e);
          toast.error("Failed to save queue configuration");
          return false;
      }
  };

  const toggleQueue = async (templateId: string) => {
      const existing = selections.find(s => s.templateId === templateId);
      let newSelections: QueueSelection[];
      
      if (existing) {
          newSelections = selections.filter(s => s.templateId !== templateId);
      } else {
          newSelections = [...selections, { templateId, enabled: true }];
      }
      
      await saveSelections(newSelections);
  };

  const openConfig = (tpl: QueueTemplate) => {
      const selection = selections.find(s => s.templateId === tpl.id);
      setConfigJql(selection?.customJql || tpl.defaultJql);
      setAiResponse(null);
      setActiveQueueId(tpl.id);
  };

  const generateAiJql = async () => {
      if (!activeQueueId) return;
      const tpl = QUEUE_TEMPLATES.find(t => t.id === activeQueueId);
      if (!tpl) return;

      setIsGenerating(true);
      setAiResponse(null);

      try {
          const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/ai-jql/generate`, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                  queueTemplate: tpl,
                  spaceContext: {
                      // Passing minimal context for now as per MVP scope
                      companyId,
                      sowId
                  }
              })
          });

          if (!res.ok) throw new Error("AI Generation failed");
          
          const data = await res.json();
          setConfigJql(data.jql);
          setAiResponse({
              explanation: data.explanation,
              confidence: data.confidence,
              assumptions: data.assumptions
          });

      } catch (e) {
          console.error(e);
          toast.error("Failed to generate JQL");
      } finally {
          setIsGenerating(false);
      }
  };

  const saveConfig = async () => {
      if (!activeQueueId) return;
      
      const existingSelection = selections.find(s => s.templateId === activeQueueId);
      let newSelections: QueueSelection[];

      if (existingSelection) {
          newSelections = selections.map(s => 
              s.templateId === activeQueueId ? { ...s, customJql: configJql } : s
          );
      } else {
          // If configuring an unselected queue, we auto-select it
          newSelections = [...selections, { templateId: activeQueueId, enabled: true, customJql: configJql }];
      }

      const success = await saveSelections(newSelections);
      if (success) setActiveQueueId(null);
  };

  // Filter Logic
  const filteredTemplates = QUEUE_TEMPLATES.filter(tpl => {
      const matchesSearch = tpl.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            tpl.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            tpl.id.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesTab = activeTab === 'all' || 
                         (activeTab === 'cross_domain' && tpl.domain === 'cross_domain') ||
                         tpl.domain === activeTab;
      return matchesSearch && matchesTab;
  });

  const getDomainIcon = (domain: string) => {
      switch(domain) {
          case 'srm': return <Zap className="w-4 h-4 text-orange-500" />;
          case 'incident': return <AlertTriangle className="w-4 h-4 text-red-500" />;
          case 'change': return <GitPullRequest className="w-4 h-4 text-purple-500" />;
          case 'cross_domain': return <Globe className="w-4 h-4 text-blue-500" />;
          default: return <Layers className="w-4 h-4 text-gray-500" />;
      }
  };

  const getIntentBadge = (intent: string) => {
    // 'my_work' | 'team_work' | 'management' | 'breaches' | 'governance'
    const format = (s: string) => s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
    return <Badge variant="outline" className="text-xs font-normal text-gray-600 capitalize">{format(intent)}</Badge>;
  };

  const activeTemplate = QUEUE_TEMPLATES.find(t => t.id === activeQueueId);
  const activeAlignment = activeTemplate ? CANONICAL_INTENT_ALIGNMENTS.find(a => a.queueId === activeTemplate.id) : null;

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Config
          </Button>
          <div className="h-6 w-px bg-gray-200" />
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <LayoutList className="w-5 h-5 text-indigo-600" />
                Queue Library
            </h1>
            <p className="text-xs text-muted-foreground">Canonical Implementation Tooling (v2026.3)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                {selections.length + custom.length} Queues Applied
            </Badge>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-6 space-y-6">
        
        {/* DOCTRINE BANNER */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 text-sm text-blue-800 items-start shadow-sm">
            <Shield className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
                <strong>Implementation Tooling Doctrine:</strong>
                <p className="mt-1 opacity-90">"Queues are implementation tooling. They operationalize intent but do not define it. PathKit asks why and for whom; it builds how."</p>
                <div className="mt-2 text-xs flex gap-2">
                    <span className="bg-blue-100 px-2 py-0.5 rounded text-blue-700 font-medium">Non-Binding</span>
                    <span className="bg-blue-100 px-2 py-0.5 rounded text-blue-700 font-medium">Advisory</span>
                </div>
            </div>
        </div>

        {/* CUSTOM QUEUES (RESTORE_PLAN R1.6) */}
        <CustomQueuesPanel
            queues={custom}
            libraryNames={QUEUE_TEMPLATES.map(t => t.name)}
            createdBy={currentUser?.name || currentUser?.email || currentUser?.id}
            onSave={saveCustom}
        />

        {/* CONTROLS */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between sticky top-[72px] z-10 bg-gray-50 py-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
                <TabsList className="bg-white border border-gray-200">
                    <TabsTrigger value="all">All Queues</TabsTrigger>
                    <TabsTrigger value="my_work">Personal</TabsTrigger>
                    <TabsTrigger value="team_work">Team Ops</TabsTrigger>
                    <TabsTrigger value="breaches">Breaches</TabsTrigger>
                    <TabsTrigger value="governance">Governance</TabsTrigger>
                    <TabsTrigger value="management">Management</TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="relative w-full md:w-64">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
                <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, ID, or logic..."
                    className="pl-9 bg-white"
                />
            </div>
        </div>

        {/* LIBRARY GRID - GROUPED BY INTENT */}
        <div className="space-y-8">
            {['my_work', 'team_work', 'breaches', 'risk', 'governance', 'problem', 'quality', 'management', 'executive'].map(intentGroup => {
                // Filter logic
                const groupTemplates = filteredTemplates.filter(t => t.intentCategory === intentGroup);
                
                // Skip empty groups unless they match tab selection (to avoid empty screens)
                if (groupTemplates.length === 0) return null;
                
                // Skip if tab is set to a specific category that isn't this one
                if (activeTab !== 'all' && activeTab !== intentGroup) return null;

                const groupTitle = intentGroup.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
                
                return (
                    <section key={intentGroup} className="space-y-4">
                        <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                {intentGroup === 'my_work' && <Users className="w-5 h-5 text-indigo-500" />}
                                {intentGroup === 'team_work' && <Briefcase className="w-5 h-5 text-blue-500" />}
                                {intentGroup === 'breaches' && <AlertTriangle className="w-5 h-5 text-red-500" />}
                                {intentGroup === 'governance' && <Shield className="w-5 h-5 text-purple-500" />}
                                {groupTitle}
                            </h2>
                            <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded-full">
                                {groupTemplates.length}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {groupTemplates.map(tpl => {
                                const isSelected = selections.some(s => s.templateId === tpl.id);
                                const selection = selections.find(s => s.templateId === tpl.id);
                                const hasCustomJql = !!selection?.customJql;
                                const alignment = CANONICAL_INTENT_ALIGNMENTS.find(a => a.queueId === tpl.id);
                                
                                // Role Affordance Badge Color
                                const getRoleBadgeColor = (role: string) => {
                                    if (role.includes('Individual')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
                                    if (role.includes('Team Lead')) return 'bg-blue-50 text-blue-700 border-blue-200';
                                    if (role.includes('Manager') || role.includes('Executive')) return 'bg-purple-50 text-purple-700 border-purple-200';
                                    return 'bg-slate-100 text-slate-600 border-slate-200';
                                };

                                return (
                                    <Card key={tpl.id} className={`transition-all ${isSelected ? 'border-indigo-500 ring-1 ring-indigo-500 shadow-md' : 'hover:border-gray-300'}`}>
                                        <CardHeader className="pb-3">
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-2">
                                                    {getDomainIcon(tpl.domain)}
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{tpl.domain.replace('_', ' ')}</span>
                                                </div>
                                                <Switch 
                                                    checked={isSelected}
                                                    onCheckedChange={() => toggleQueue(tpl.id)}
                                                />
                                            </div>
                                            <CardTitle className="mt-2 text-base flex items-center gap-2">
                                                {tpl.name}
                                            </CardTitle>
                                            <CardDescription className="line-clamp-2 text-xs h-8 leading-snug">{tpl.description}</CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <div className="flex flex-wrap gap-2">
                                                <Badge variant="outline" className={`text-[10px] font-normal border ${getRoleBadgeColor(tpl.audienceRole)}`}>
                                                    {tpl.audienceRole}
                                                </Badge>
                                                
                                                {tpl.maturityLevel === 'advanced' && (
                                                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Advanced</Badge>
                                                )}
                                            </div>

                                            <div className="pt-3 border-t border-gray-100 space-y-2">
                                                <div className="flex items-start justify-between text-xs text-gray-500">
                                                    <span className="flex items-center gap-1 shrink-0"><Filter className="w-3 h-3" /> Logic</span>
                                                    <HoverCard>
                                                        <HoverCardTrigger>
                                                            <span className="text-right line-clamp-1 border-b border-dotted border-gray-400 cursor-help max-w-[150px]">{tpl.canonicalFilterLogic}</span>
                                                        </HoverCardTrigger>
                                                        <HoverCardContent className="w-80 bg-slate-900 text-slate-50 border-slate-800 p-3">
                                                            <div className="space-y-2">
                                                                <div>
                                                                    <span className="text-xs text-slate-400 uppercase font-bold">Canonical Logic</span>
                                                                    <div className="text-sm mt-1">{tpl.canonicalFilterLogic}</div>
                                                                </div>
                                                            </div>
                                                        </HoverCardContent>
                                                    </HoverCard>
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-gray-500">
                                                    <span className="flex items-center gap-1"><LayoutList className="w-3 h-3" /> Columns</span>
                                                    <span>{tpl.defaultColumns.length} Fields</span>
                                                </div>
                                                
                                                {alignment && (
                                                    <div className="mt-2 p-2 bg-indigo-50/50 rounded border border-indigo-100/50 text-xs group-hover:bg-indigo-50 transition-colors">
                                                        <div className="flex items-center gap-1.5 text-indigo-700 font-medium mb-1">
                                                            <LayoutDashboard className="w-3 h-3" />
                                                            <span>Dashboard Intent</span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1 mb-1.5">
                                                            {alignment.dashboardIntentTags.map(tag => (
                                                                <span key={tag} className="px-1.5 py-0.5 bg-white border border-indigo-100 rounded text-[10px] text-indigo-600 shadow-sm">
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </CardContent>
                                        <CardFooter className="pt-0 pb-3">
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                className={`w-full text-xs h-8 ${hasCustomJql ? 'border-indigo-200 text-indigo-700 bg-indigo-50' : ''}`}
                                                onClick={() => openConfig(tpl)}
                                            >
                                                <Settings className="w-3 h-3 mr-2" />
                                                {hasCustomJql ? 'Configure JQL (Custom)' : 'Configure JQL'}
                                            </Button>
                                        </CardFooter>
                                    </Card>
                                );
                            })}
                        </div>
                    </section>
                );
            })}
        </div>

        {/* CONFIG DIALOG */}
        <Dialog open={!!activeQueueId} onOpenChange={(open) => {
            if (!open) {
                setActiveQueueId(null);
                setShowJql(false);
            }
        }}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        Configure Queue: {activeTemplate?.name}
                    </DialogTitle>
                    <DialogDescription>
                        Review and customize the logic for this queue.
                    </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => setShowJql(!showJql)}
                                className="text-xs text-muted-foreground hover:text-indigo-600 p-0 h-auto"
                            >
                                {showJql ? 'Hide JQL (Advanced)' : 'View / Edit JQL (Advanced)'}
                            </Button>
                            
                            {showJql && (
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={generateAiJql} 
                                    disabled={isGenerating}
                                    className="h-7 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                                >
                                    {isGenerating ? (
                                        <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                    ) : (
                                        <Sparkles className="w-3 h-3 mr-2" />
                                    )}
                                    Generate Draft JQL (AI-assisted)
                                </Button>
                            )}
                        </div>
                        
                        {/* AI RESULT BANNER */}
                        {aiResponse && (
                            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 text-sm space-y-2">
                                <div className="flex items-center gap-2 font-medium text-indigo-900">
                                    <Sparkles className="w-4 h-4 text-indigo-600" />
                                    This is an AI-assisted draft based on canonical intent.
                                </div>
                                <p className="text-indigo-800 text-xs">{aiResponse.explanation}</p>
                                
                                <div className="flex items-center gap-4 mt-2">
                                    <Badge variant={aiResponse.confidence === 'High' ? 'default' : 'secondary'} className={aiResponse.confidence === 'High' ? 'bg-green-600' : 'bg-amber-500'}>
                                        {aiResponse.confidence} Confidence
                                    </Badge>
                                </div>

                                {aiResponse.assumptions.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-indigo-200">
                                        <div className="text-xs font-semibold text-indigo-900 mb-1 flex items-center gap-1">
                                            <AlertCircle className="w-3 h-3" />
                                            Review Required - Assumptions Made:
                                        </div>
                                        <ul className="list-disc list-inside text-xs text-indigo-800">
                                            {aiResponse.assumptions.map((a, i) => (
                                                <li key={i}>{a}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {showJql ? (
                            <Textarea 
                                value={configJql} 
                                onChange={(e) => setConfigJql(e.target.value)}
                                className="font-mono text-xs min-h-[100px]"
                                placeholder="Enter JQL query..."
                            />
                        ) : (
                            <div className="p-4 bg-gray-50 border rounded-md text-sm text-gray-600">
                                <p className="mb-2 font-medium">Queue Logic:</p>
                                <p>{activeTemplate?.description}</p>
                                <p className="mt-2 text-xs text-muted-foreground">
                                    Canonical Logic: <span className="italic">{activeTemplate?.canonicalFilterLogic}</span>
                                </p>
                            </div>
                        )}
                    </div>

                    {/* INTENT ALIGNMENT SECTION */}
                    {activeAlignment && (
                        <div className="rounded-lg border bg-slate-50 p-4 space-y-3">
                             <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                                <LayoutDashboard className="w-4 h-4 text-indigo-600" />
                                Recommended Dashboard Alignment
                             </div>
                             <div className="flex flex-wrap gap-2">
                                {activeAlignment.dashboardIntentTags.map(tag => (
                                    <Badge key={tag} variant="secondary" className="bg-white border-slate-200 text-slate-600">
                                        {tag}
                                    </Badge>
                                ))}
                             </div>
                             <p className="text-xs text-slate-600 leading-relaxed">
                                {activeAlignment.rationale}
                             </p>
                             <div className="flex items-center gap-2 text-[10px] text-slate-400 pt-2 border-t border-slate-200">
                                <Shield className="w-3 h-3" />
                                Advisory Only • Non-Binding
                             </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => setActiveQueueId(null)}>Cancel</Button>
                    <Button onClick={saveConfig}>Save Configuration</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

      </main>
    </div>
  );
}
